import React from "react";
import { withRouter } from "react-router";
import { connect } from "react-redux";
import { IStore } from "reducers";
import {
    clearCurrentPromotion,
    bulkDeletePromotions,
    addPromotion,
    fetchCurrentPromotion,
    promotionsListFetch,
    bulkEditPromotions,
    fetchFilteredPromotions,
} from "actions/promotions";
import { format } from "lib/globalize-util";
import Page from "components/page";

import { DeleteConfirmationModal } from "containers/admin/promotions/modals/DeleteConfirmationModal";
import { promotionsService } from "containers/admin/promotions/services/promotions";
import { add as snackbarAdd } from "actions/snackbars";
import DuplicateModalContainer from "containers/admin/promotions/modals/DuplicateModal";
import { CreateButton, useStyles } from "./styles";
import { generateBreadcrumbs } from "./helpers/generateBreadcrumbs";
import { DATATEST } from "constants/integration";
import { IPromotionsList, type IPromotionsListFilters } from "./interfaces";
import { IListTableHandlers } from "./list-grid/list-grid-select";
import PromotionGrid from "./list-grid/promotion-grid";

import Slide from "@material-ui/core/Slide";
import { Paper } from "@material-ui/core";
import Delete from "@material-ui/icons/Delete";
import { PriceEntity } from "../entity/price-entity/promotions.types";
import { THRESHOLD_FOLDER, THRESHOLD_SUFFIX } from "containers/admin/promotions/utils/consts";
import { PromotionTypeModal } from "../components/promotion-type-modal";
import { useFlags } from "launchdarkly-react-client-sdk";

export const PROMOTIONS_LIST_KEY = "PROMOTIONS_LIST_VIEW";

enum Direction {
    DOWN = "down",
}

export const PromotionsList: React.FC<IPromotionsList> = ({
    merchantId,
    onSnackbarAdd,
    onFetchPromotionsList,
    onFetchCurrentPromotion,
    onClearCurrentPromotion,
    bulkDeletePromotionsFromStore,
    addPromotionToStore,
    onBulkEditPromotions,
    onFilterChange,
    promotions,
    brandId,
    router,
}) => {
    const classes = useStyles();
    const flags = useFlags();

    React.useEffect(() => {
        if (merchantId) {
            onFetchPromotionsList(merchantId).then(() => {
                if (promotions?.filters && Object.keys(promotions?.filters)?.length > 0 && promotions?.shouldPersist) {
                    handleFilterChange(promotions.filters);
                }
            });
        }
    }, [merchantId, onFetchPromotionsList]);

    React.useEffect(() => {
        onClearCurrentPromotion();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const PROMOTIONS_TYPES = [
        {
            name: format("Order promotion"),
            description: format("Set order criteria for promotion eligibility"),
            value: "order",
        },
        {
            name: format("Product promotion"),
            description: format("Set product criteria for promotion eligibility"),
            value: "product",
        },
    ];

    const [showDeleteModal, setShowDeleteModal] = React.useState<boolean>(false);
    const [showDuplicateModal, setShowDuplicateModal] = React.useState<boolean>(false);
    const [idForDelete, setIdForDelete] = React.useState<string>("");
    const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
    const [showBulkActions, setShowBulkActions] = React.useState<boolean>(false);
    const [isBulkAction, setIsBulkAction] = React.useState<boolean>(false);
    const [showPromotionTypeModal, setShowPromotionTypeModal] = React.useState<boolean>(false);
    const [isLoading, setIsLoading] = React.useState<boolean>(true);

    React.useEffect(() => {
        setIsLoading(promotions.isFetching);
    }, [promotions.isFetching]);

    const closeDeleteModal = React.useCallback(() => {
        setShowDeleteModal(false);
        setIdForDelete("");
        setShowBulkActions(false);
        setSelectedIds([]);
    }, [setShowDeleteModal, setIdForDelete]);

    const closeDuplicateModal = React.useCallback(() => {
        setShowDuplicateModal(false);
        onClearCurrentPromotion();
    }, [onClearCurrentPromotion]);

    const onSuccessDuplicated = React.useCallback(
        (duplicatedEntity) => addPromotionToStore(duplicatedEntity),
        [addPromotionToStore]
    );
    const breadcrumbs = React.useMemo(() => generateBreadcrumbs(brandId), [brandId]);

    React.useMemo(() => setShowBulkActions(!!selectedIds.length), [selectedIds]);

    const handleBulkDeleteAction = React.useCallback(() => {
        setShowDeleteModal(true);
        setIsBulkAction(true);
    }, []);

    const generalDeleteAction = React.useCallback(() => {
        const arr = isBulkAction ? selectedIds : [idForDelete];
        const thresholdArr = arr.map((id) => `${id}${THRESHOLD_SUFFIX}`);

        promotionsService
            .bulkDeleteEntities(arr, merchantId)
            .catch(() => onSnackbarAdd({ message: format("Error delete promotion. Try again later") }));
        promotionsService
            .bulkDeleteEntities(thresholdArr, merchantId, THRESHOLD_FOLDER)
            .catch(() => onSnackbarAdd({ message: format("Error delete promotion. Try again later") }));
        bulkDeletePromotionsFromStore(arr);

        if (isBulkAction) {
            setSelectedIds([]);
        } else {
            const filtered = selectedIds.filter((x) => x !== idForDelete);
            setSelectedIds(filtered);
        }

        closeDeleteModal();
    }, [
        bulkDeletePromotionsFromStore,
        onSnackbarAdd,
        selectedIds,
        closeDeleteModal,
        merchantId,
        idForDelete,
        isBulkAction,
    ]);

    const onCreatePromotionClick = React.useCallback(
        (value) => {
            switch (value) {
                case "order":
                    router.push(`/brand/${brandId}/admin/promotions/manage-promotions/order`);
                    break;
                case "product":
                    router.push(`/brand/${brandId}/admin/promotions/manage-promotions/product`);
                    break;
                default:
                    break;
            }
        },
        [brandId, router]
    );

    const onTogglePromotionTypeModal = React.useCallback(
        () => setShowPromotionTypeModal(!showPromotionTypeModal),
        [showPromotionTypeModal]
    );

    const actions = React.useMemo(
        () => (
            <div>
                <CreateButton
                    onClick={onTogglePromotionTypeModal}
                    disabled={false}
                    data-test={DATATEST.PROMOTIONS.CREATE_NEW_PROMOTION}
                >
                    {format("Create Promotion")}
                </CreateButton>
                <PromotionTypeModal
                    promotionTypes={PROMOTIONS_TYPES}
                    isModalOpen={showPromotionTypeModal}
                    onToggle={onTogglePromotionTypeModal}
                    onClick={onCreatePromotionClick}
                    data-test={DATATEST.PROMOTIONS.PROMOTION_TYPE_MODAL_WRAPPER}
                />
            </div>
        ),
        [onTogglePromotionTypeModal, onCreatePromotionClick, showPromotionTypeModal, PROMOTIONS_TYPES]
    );

    const handleEditAction = (ids: string[]) => {
        const actions = promotions.results.filter((res) => res.id === ids[0])[0].actions;

        const promotionType =
            // PromotionScope.PRICE_ACTION_SCOPE_TOTAL causes errors so using string value
            actions[0]?.price_action?.scope === "PRICE_ACTION_SCOPE_TOTAL" ? "order" : "product";

        router.push(`/brand/${brandId}/admin/promotions/manage-promotions/${promotionType}/${ids[0]}`);
    };

    const handleDuplicateAction = (ids: string[]) => {
        if (ids.length === 1) {
            onFetchCurrentPromotion(ids[0], merchantId);
            setShowDuplicateModal(true);
        }
    };

    const handleDeleteAction = (ids: string[]) => {
        if (ids.length === 1) {
            setShowDeleteModal(true);
            setIdForDelete(ids[0]);
            setIsBulkAction(false);
        } else {
            setSelectedIds(ids);
            handleBulkDeleteAction();
        }
    };

    const handleActivateAction = (ids: string[]) => {
        handleBulkEditAction(ids, true);
    };

    const handleDeactivateAction = (ids: string[]) => {
        handleBulkEditAction(ids, false);
    };

    const handleBulkEditAction = React.useCallback(
        (ids: string[], enabled: boolean) => {
            setIsLoading(true);
            const bulkEntities = promotions.results
                ?.filter((promo) => ids.includes(promo.id))
                ?.map((entity) => ({ ...entity, enabled }));

            promotionsService
                .bulkUpdateEntities(bulkEntities, merchantId, enabled)
                .then((data) => onBulkEditPromotions(data.price_entities))
                .catch(() => setIsLoading(false))
                .then(() => setIsLoading(false));
        },
        [promotions]
    );

    const singleSelectedRowActionHandlers: IListTableHandlers[] = [
        {
            label: "Edit",
            handler: handleEditAction,
            dataTest: DATATEST.PROMOTIONS.LIST_EDIT_ICON,
        },
        {
            label: "Duplicate",
            handler: handleDuplicateAction,
            dataTest: DATATEST.PROMOTIONS.LIST_FILECOPY_ICON,
        },
        {
            label: "Delete",
            handler: handleDeleteAction,
            dataTest: DATATEST.PROMOTIONS.LIST_DELETE_ICON,
        },
        {
            label: "Activate",
            handler: handleActivateAction,
            dataTest: DATATEST.PROMOTIONS.LIST_ACTIVATE_ICON,
        },
        {
            label: "Deactivate",
            handler: handleDeactivateAction,
            dataTest: DATATEST.PROMOTIONS.LIST_DEACTIVATE_ICON,
        },
    ];

    const multiSelectedRowActionHandlers: IListTableHandlers[] = [
        {
            label: "Delete",
            handler: handleDeleteAction,
            dataTest: DATATEST.PROMOTIONS.LIST_DELETE_ICON,
        },
        {
            label: "Activate",
            handler: handleActivateAction,
            dataTest: DATATEST.PROMOTIONS.LIST_ACTIVATE_ICON,
        },
        {
            label: "Deactivate",
            handler: handleDeactivateAction,
            dataTest: DATATEST.PROMOTIONS.LIST_DEACTIVATE_ICON,
        },
    ];

    const handleFilterChange = (filters: IPromotionsListFilters) => {
        setIsLoading(true);
        onFilterChange(merchantId, filters, flags.promotionsEnablesElasticSearchFilteringNu64701);
        setIsLoading(false);
    };

    return (
        <>
            <Slide direction={Direction.DOWN} in={showBulkActions} mountOnEnter unmountOnExit>
                <Paper elevation={4} className={classes.paper}>
                    <div className={classes.bulkActionsContainer}>
                        <Delete className={classes.bulkActionsIcon} onClick={handleBulkDeleteAction} />
                    </div>
                </Paper>
            </Slide>
            <Page
                title={format("Manage Promotions")}
                showTitle={true}
                className={classes.wrapper}
                actions={actions}
                breadcrumbs={breadcrumbs}
                openNavSection="admin"
            >
                <div className="listRowsWrapper">
                    <PromotionGrid
                        singleSelectedRowActionHandlers={singleSelectedRowActionHandlers}
                        multiSelectedRowActionHandlers={multiSelectedRowActionHandlers}
                        isLoading={isLoading}
                        onFilterChange={handleFilterChange}
                    />
                    <DeleteConfirmationModal
                        data-test={DATATEST.PROMOTIONS.DELETE_MODAL}
                        open={showDeleteModal}
                        onConfirm={generalDeleteAction}
                        onClose={closeDeleteModal}
                        isBulkAction={isBulkAction}
                    />
                    <DuplicateModalContainer
                        data-test={DATATEST.PROMOTIONS.DUPLICATE_MODAL}
                        open={showDuplicateModal}
                        onClose={closeDuplicateModal}
                        onSuccess={onSuccessDuplicated}
                    />
                </div>
            </Page>
        </>
    );
};

const mapStateToProps = (state: IStore): Partial<IPromotionsList> => ({
    currentPromotion: state.promotion.entity,
    promotions: state.promotions.collections[PROMOTIONS_LIST_KEY] || {
        results: [],
        isFetching: false,
        hasMoreResults: false,
        nextPage: undefined,
        shouldPersist: false,
    },
    brandId: state.scope.brand.get("_id"),
    merchantId: state.scope.brand.get("merchant_id"),
});

const mapDispatchToProps = (dispatch) => ({
    onClearCurrentPromotion: () => dispatch(clearCurrentPromotion()),
    onFetchPromotionsList: (merchantId, token) => dispatch(promotionsListFetch(PROMOTIONS_LIST_KEY, merchantId, token)),
    onFetchCurrentPromotion: (promotionId, merchantId) => dispatch(fetchCurrentPromotion(promotionId, merchantId)),
    bulkDeletePromotionsFromStore: (ids: string[]) => dispatch(bulkDeletePromotions(ids)),
    addPromotionToStore: (duplicate: PriceEntity) => dispatch(addPromotion(duplicate)),
    onSnackbarAdd: (params: { message: string }) => dispatch(snackbarAdd(params)),
    onBulkEditPromotions: (entities: PriceEntity[]) => dispatch(bulkEditPromotions(entities)),
    onFilterChange: (
        merchantId,
        filters: IPromotionsListFilters,
        promotionsEnablesElasticSearchFilteringNu64701: boolean
    ) => dispatch(fetchFilteredPromotions(merchantId, filters, promotionsEnablesElasticSearchFilteringNu64701)),
});

export const PromotionsListContainer = withRouter(connect(mapStateToProps, mapDispatchToProps)(PromotionsList));
export default PromotionsListContainer;
